import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Menu,
  MenuProps,
  alpha
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { SubjectService } from '../../services/subjectService';
import { AttendanceService } from '../../services/attendanceService';
import { ExportService } from '../../services/exportService';
import { Subject } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface AttendanceData {
  date: string;
  presentCount: number;
  absentCount: number;
  attendanceRate: number;
  markedBy: string;
  markedByName: string;
  markedAt: Date;
  students: {
    id: string;
    name: string;
    rollNumber: string;
    status: 'Present' | 'Absent';
    remarks: string;
  }[];
}

export function SubjectAttendanceView() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [attendanceData, setAttendanceData] = useState<AttendanceData[]>([]);
  const [subjectStats, setSubjectStats] = useState({
    totalSessions: 0,
    averageAttendanceRate: 0,
    subjectName: '',
    subjectCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<AttendanceData | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      loadSubjectAttendance();
    }
  }, [selectedSubject, startDate, endDate]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      const result = await SubjectService.getSubjects(100);
      setSubjects(result.data);
    } catch (error) {
      console.error('Error loading subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjectAttendance = async () => {
    if (!selectedSubject) return;

    try {
      setLoading(true);
      const result = await AttendanceService.getSubjectAttendanceFromSubjectsCollection(
        selectedSubject,
        startDate || undefined,
        endDate || undefined
      );

      setAttendanceData(result.attendanceData);
      setSubjectStats({
        totalSessions: result.totalSessions,
        averageAttendanceRate: result.averageAttendanceRate,
        subjectName: result.subjectName,
        subjectCode: result.subjectCode
      });
    } catch (error) {
      console.error('Error loading subject attendance:', error);
      setAttendanceData([]);
      setSubjectStats({
        totalSessions: 0,
        averageAttendanceRate: 0,
        subjectName: '',
        subjectCode: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (session: AttendanceData) => {
    setSelectedSession(session);
    setDetailDialog(true);
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportToPDF = async () => {
    try {
      setExporting(true);
      handleExportClose();
      
      // Format data for export - add missing fields
      const exportData = attendanceData.flatMap(session => 
        session.students.map(student => ({
          id: `${selectedSubject}-${student.id}-${session.date}`,
          studentId: student.id,
          studentName: student.name,
          rollNumber: student.rollNumber,
          classId: 'unknown', // We don't have class info in this context
          className: 'Subject Attendance',
          date: new Date(session.date),
          status: student.status
        }))
      );

      const fileName = `${subjectStats.subjectCode}_attendance_report`;
      
      // Use custom PDF export
      await exportToPDF(exportData, fileName);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      handleExportClose();
      
      // Format data for export - add missing fields
      const exportData = attendanceData.flatMap(session => 
        session.students.map(student => ({
          id: `${selectedSubject}-${student.id}-${session.date}`,
          studentId: student.id,
          studentName: student.name,
          rollNumber: student.rollNumber,
          classId: 'unknown', // We don't have class info in this context
          className: 'Subject Attendance',
          date: new Date(session.date),
          status: student.status
        }))
      );

      const fileName = `${subjectStats.subjectCode}_attendance_report`;
      
      // Use custom Excel export
      await exportToExcel(exportData, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Custom PDF export function
  const exportToPDF = async (data: any[], fileName: string) => {
    // Dynamic import to avoid bundle size issues
    const jsPDF = (await import('jspdf')).default;
    const autoTable = (await import('jspdf-autotable')).default;

    const doc = new jsPDF('landscape');
    
    // Add title
    doc.setFontSize(18);
    doc.text('Subject Attendance Report', 14, 22);
    doc.setFontSize(12);
    doc.text(`Subject: ${subjectStats.subjectName} (${subjectStats.subjectCode})`, 14, 32);
    
    // Add generation date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 40);
    doc.text(`Total Records: ${data.length}`, 14, 46);

    // Group data by student and date
    const studentMap = new Map();
    const allDates = new Set();
    
    data.forEach(record => {
      const studentKey = `${record.studentId}-${record.studentName}`;
      const dateKey = record.date.toISOString().split('T')[0];
      
      allDates.add(dateKey);
      
      if (!studentMap.has(studentKey)) {
        studentMap.set(studentKey, {
          studentId: record.studentId,
          studentName: record.studentName,
          rollNumber: record.rollNumber,
          attendanceByDate: new Map()
        });
      }
      
      const student = studentMap.get(studentKey);
      student.attendanceByDate.set(dateKey, record.status);
    });
    
    // Sort dates and limit for PDF
    const sortedDates = Array.from(allDates).sort().slice(0, 10);
    
    // Create headers
    const headers: string[] = ['Student ID', 'Student Name', 'Roll Number', ...sortedDates.map(date => String(date))];
    
    // Create table data
    const tableData: (string | number)[][] = [];
    studentMap.forEach(student => {
      const row: (string | number)[] = [
        student.studentId,
        student.studentName,
        student.rollNumber,
        ...sortedDates.map(date => {
          const status = student.attendanceByDate.get(date) || '';
          return status === 'Present' ? 'P' : status === 'Absent' ? 'A' : '';
        })
      ];
      tableData.push(row);
    });

    // Add table
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 55,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 25 },
      },
      margin: { top: 55 }
    });

    if (sortedDates.length < allDates.size) {
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.text(`Note: Showing first ${sortedDates.length} dates of ${allDates.size} total dates. P=Present, A=Absent`, 14, finalY);
    }

    doc.save(`${fileName}.pdf`);
  };

  // Custom Excel export function
  const exportToExcel = async (data: any[], fileName: string) => {
    // Dynamic import
    const XLSX = await import('xlsx');

    // Group data by student and date
    const studentMap = new Map();
    const allDates = new Set();
    
    data.forEach(record => {
      const studentKey = `${record.studentId}-${record.studentName}`;
      const dateKey = record.date.toISOString().split('T')[0];
      
      allDates.add(dateKey);
      
      if (!studentMap.has(studentKey)) {
        studentMap.set(studentKey, {
          studentId: record.studentId,
          studentName: record.studentName,
          rollNumber: record.rollNumber,
          attendanceByDate: new Map()
        });
      }
      
      const student = studentMap.get(studentKey);
      student.attendanceByDate.set(dateKey, record.status);
    });
    
    // Sort dates
    const sortedDates = Array.from(allDates).sort();
    
    // Create header row
    const headers = ['Student ID', 'Student Name', 'Roll Number', ...sortedDates];
    
    // Create data rows
    const worksheetData = [headers];
    
    studentMap.forEach(student => {
      const row = [
        student.studentId,
        student.studentName,
        student.rollNumber,
        ...sortedDates.map(date => student.attendanceByDate.get(date) || '')
      ];
      worksheetData.push(row);
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const colWidths = [
      { wch: 12 }, // Student ID
      { wch: 25 }, // Student Name
      { wch: 15 }, // Roll Number
      ...sortedDates.map(() => ({ wch: 12 })) // Date columns
    ];
    worksheet['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Subject Attendance View
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Select Subject</InputLabel>
              <Select
                value={selectedSubject}
                label="Select Subject"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a subject</em>
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.code} - {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSubjectAttendance}
              disabled={!selectedSubject || loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {selectedSubject && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6" color="primary">
                        {subjectStats.subjectCode}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {subjectStats.subjectName}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {subjectStats.totalSessions}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Sessions
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {subjectStats.averageAttendanceRate >= 75 ? (
                      <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                    ) : (
                      <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
                    )}
                    <Box>
                      <Typography 
                        variant="h4" 
                        color={subjectStats.averageAttendanceRate >= 75 ? 'success.main' : 'error.main'}
                      >
                        {subjectStats.averageAttendanceRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Average Attendance
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PeopleIcon color="secondary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="secondary.main">
                        {attendanceData.length > 0 ? attendanceData[0].students.length : 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Students
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Attendance Sessions Table */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Attendance Sessions</Typography>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
                size="small"
                disabled={attendanceData.length === 0 || exporting}
                onClick={handleExportClick}
                sx={{
                  borderColor: alpha('#1976d2', 0.5),
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: alpha('#1976d2', 0.04),
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : attendanceData.length === 0 ? (
              <Alert severity="info">
                No attendance data found for the selected subject and date range.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Present</TableCell>
                      <TableCell align="center">Absent</TableCell>
                      <TableCell align="center">Attendance Rate</TableCell>
                      <TableCell>Marked By</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendanceData.map((session, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(session.date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={session.presentCount}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={session.absentCount}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${session.attendanceRate}%`}
                            color={getAttendanceColor(session.attendanceRate)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {session.markedByName || session.markedBy}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {session.markedAt.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(session)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Session Details Dialog */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Attendance Details - {selectedSession && formatDate(selectedSession.date)}
        </DialogTitle>
        <DialogContent>
          {selectedSession && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    Present Students
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {selectedSession.presentCount}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    Absent Students
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {selectedSession.absentCount}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Student List
              </Typography>
              <List>
                {selectedSession.students.map((student, index) => (
                  <React.Fragment key={student.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body1">
                              {student.name} ({student.rollNumber})
                            </Typography>
                            <Chip
                              label={student.status}
                              color={student.status === 'Present' ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={student.remarks && `Remarks: ${student.remarks}`}
                      />
                    </ListItem>
                    {index < selectedSession.students.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={handleExportToPDF}
          sx={{ 
            py: 1.5, 
            px: 2,
            '&:hover': { 
              backgroundColor: alpha('#d32f2f', 0.08) 
            }
          }}
        >
          <PdfIcon sx={{ mr: 2, color: '#d32f2f' }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Export to PDF
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Formatted attendance report
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={handleExportToExcel}
          sx={{ 
            py: 1.5, 
            px: 2,
            '&:hover': { 
              backgroundColor: alpha('#2e7d32', 0.08) 
            }
          }}
        >
          <ExcelIcon sx={{ mr: 2, color: '#2e7d32' }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Export to Excel
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Spreadsheet with pivot data
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Custom export functions
const exportToPDF = async (data: any[], fileName: string) => {
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(20);
  doc.text('Subject Attendance Report', 14, 22);
  
  // Add timestamp
  doc.setFontSize(12);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);
  
  // Prepare table data
  const tableData = data.map(record => [
    record.studentId,
    record.studentName,
    record.rollNumber,
    new Date(record.date).toLocaleDateString(),
    record.status === 'present' ? 'Present' : 'Absent'
  ]);

  // Add table
  autoTable(doc, {
    head: [['Student ID', 'Student Name', 'Roll Number', 'Date', 'Status']],
    body: tableData,
    startY: 40,
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [63, 81, 181],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  });

  doc.save(`${fileName}.pdf`);
};

const exportToExcel = async (data: any[], fileName: string) => {
  const XLSX = await import('xlsx');
  
  // Prepare Excel data
  const excelData = data.map(record => ({
    'Student ID': record.studentId,
    'Student Name': record.studentName,
    'Roll Number': record.rollNumber,
    'Date': new Date(record.date).toLocaleDateString(),
    'Status': record.status === 'present' ? 'Present' : 'Absent'
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

  // Download the file
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}; 